/**
 * Product field registry — requiredness as DATA, not code (S0.1 col-J rebase).
 *
 * One row per canonical product field: which create-form section it lives in
 * (house-board 10-section skeleton), its MoSCoW triage from the 0708 xlsx
 * (`20260708 產品主檔 欄位.xlsx` Data Dictionary **col J** `MoSCoW (R&D)` — THE
 * Must authority: **74** Must rows, joined BY LABEL never by position; col K
 * is stale and must not be cited as field freeze), whether it is required at
 * create (exactly **8** keys: Identity + Units + Classification), which
 * Tier-2 readiness gap group it feeds (Product Ready 產品就緒 = create-8 +
 * 3 descriptions + basePrice + costingMethod — enrichment / process-entry
 * gate only, never a third MoSCoW), and its NetSuite field mapping.
 *
 * The registry drives:
 * - red asterisks in the create wizard (create-required only),
 * - `computeProductReadiness` (the read-side readiness gate — replaces the
 *   codex `computeOccStatus`),
 * - render-blocked fields (`blocked: true` → purple `BlockedFieldCallout`,
 *   non-editable, "no guess"),
 * - the required-iff-共用 attention state (`requiredIfShared` +
 *   `getAttentionRules`).
 *
 * Col-J conditional directive → named mechanism map (FD6):
 * - 看SCN狀況 (DD!19 Alternate SKU) → required-iff-共用: `requiredIfShared`
 *   on `scn`, keyed on optional `product.isShared`. Shared + unlinked reports
 *   an ATTENTION state (`getAttentionRules`), NEVER a readiness gap — SCN
 *   never gates 產品就緒 (escalation = Q-O2).
 * - C108 inline 【MUST】 → `ageGroup` is `systemAssigned`, system-computed
 *   from user-entered `ageStartYear`/`ageEndYear` (pure `computeAgeGroup`,
 *   S1.1); its `isFilled` derives from the year range, not the stored label.
 * - 看Pricing Engine如何設計 (DD!47 List Price) → the Wave 3–5 pricing-engine
 *   surfaces; interacts with the `basePrice` PENDING-RULING row below.
 * - 使用者選擇 (DD!128–131 currency tabs) → per-user persisted currency-tab
 *   affordance in the pricing matrix UI (no registry rows).
 * - 產品管控中心應該查得到 (DD!183–187) → executable visibility test against
 *   `ControlCenterLinksSection` (S8.2 closure suite; no registry rows).
 * - COULD，STERLING有用到，調貨用 (DD!227–229) → moscow `could` (rows below).
 *   S8.1 (財務資料 confirm pass) lifted their `blocked` flags TOGETHER with
 *   `accountingPlaceholdersSchema` coverage — they render as ordinary could
 *   placeholder rows now, not `BlockedFieldCallout` (the callout path stays
 *   for any future blocked row).
 *
 * A Field Decision Register change (e.g. a Melissa ruling on any of the five
 * `PENDING-RULING(0708-J)` over-claims) is a ONE-LINE data edit here — no
 * type churn.
 */

import type { Product, ProductReadiness, ProductReadinessGap } from '@/types/api/product';

export type ProductFieldSection =
  | 'identity'
  | 'units'
  | 'classification'
  | 'descriptions'
  | 'attributes'
  | 'fitment'
  | 'manufacturing'
  | 'commercial'
  | 'accounting'
  | 'planning'
  | 'relations';

/** 0708 xlsx col-J（MoSCoW (R&D)）triage buckets. */
export type ProductFieldMoscow = 'must' | 'should' | 'could' | 'wont' | 'clarify';

export interface ProductFieldRule {
  /** Canonical `Product` field key (accounting refs use `accounting.<key>`). */
  key: string;
  section: ProductFieldSection;
  moscow: ProductFieldMoscow;
  /** Mandatory to save Create (red asterisk). */
  requiredAtCreate: boolean;
  /** Tier-2 gap group this field feeds; absent = never gates readiness. */
  requiredForReady?: ProductReadinessGap;
  /** NetSuite item field id (read-side Tier-1 mapping). */
  netSuiteField?: string;
  /** System-assigned — never enterable on any form. */
  systemAssigned?: boolean;
  /** Render-blocked → purple BlockedFieldCallout, non-editable (no guess). */
  blocked?: boolean;
  /** i18n label key (registered en+zh by the UI slices). */
  labelKey?: string;
  /** Presence override; default = non-empty scalar / non-empty array. */
  isFilled?: (product: Product) => boolean;
  /**
   * required-iff-共用 (DD!19 看SCN狀況, FD6): when `product.isShared` is true
   * and the field is unfilled, the row reports an ATTENTION state via
   * `getAttentionRules` — never a create block, never a readiness gap.
   */
  requiredIfShared?: boolean;
}

const filledString = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const filledPositiveNumber = (value: number | null | undefined): boolean =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const PRODUCT_FIELD_REGISTRY: readonly ProductFieldRule[] = [
  // -- Identity 識別 --------------------------------------------------------
  {
    key: 'internalProductId',
    section: 'identity',
    moscow: 'must',
    requiredAtCreate: false,
    systemAssigned: true,
    labelKey: 'products:fields.internalProductId',
  },
  {
    key: 'sku',
    section: 'identity',
    moscow: 'must',
    requiredAtCreate: true,
    netSuiteField: 'itemid',
    labelKey: 'products:fields.sku',
  },
  {
    key: 'displayName',
    section: 'identity',
    moscow: 'must',
    requiredAtCreate: true,
    netSuiteField: 'displayname',
    labelKey: 'products:fields.displayName',
  },
  {
    key: 'netSuiteItemId',
    section: 'identity',
    moscow: 'should',
    requiredAtCreate: false,
    systemAssigned: true,
    netSuiteField: 'internalid',
    labelKey: 'products:fields.netSuiteItemId',
  },
  {
    // 0708-J promotion could→must; J renamed the label to 'UPC Code / QR CODE'
    key: 'upcCode',
    section: 'identity',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'upccode',
    labelKey: 'products:fields.upcCode',
  },
  {
    // DD!19 Alternate SKU 替代SKU, col-J = 看SCN狀況 → required-iff-共用
    // mechanism (C19: shared-SKU purpose, obsolete once SCN runs). Attention
    // when shared + unlinked; SCN NEVER gates 產品就緒.
    key: 'scn',
    section: 'identity',
    moscow: 'could',
    requiredAtCreate: false,
    requiredIfShared: true,
    labelKey: 'products:fields.scn',
  },

  // -- Units 單位 (cascade C1, default PCS — sticky R8) ---------------------
  {
    key: 'unitsTypeId',
    section: 'units',
    moscow: 'must',
    requiredAtCreate: true,
    netSuiteField: 'unitstype',
    labelKey: 'products:fields.unitsType',
  },
  {
    key: 'stockUnit',
    section: 'units',
    moscow: 'must',
    requiredAtCreate: true,
    netSuiteField: 'stockunit',
    labelKey: 'products:fields.stockUnit',
  },
  {
    key: 'purchaseUnit',
    section: 'units',
    moscow: 'must',
    requiredAtCreate: true,
    netSuiteField: 'purchaseunit',
    labelKey: 'products:fields.purchaseUnit',
  },
  {
    key: 'saleUnit',
    section: 'units',
    moscow: 'must',
    requiredAtCreate: true,
    netSuiteField: 'saleunit',
    labelKey: 'products:fields.saleUnit',
  },

  // -- Classification 分類 (cascade C2) -------------------------------------
  {
    key: 'subsidiaryId',
    section: 'classification',
    moscow: 'must',
    requiredAtCreate: true,
    netSuiteField: 'subsidiary',
    labelKey: 'products:fields.subsidiary',
  },
  {
    // 0708-J promotion should→must
    key: 'includeChildren',
    section: 'classification',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'includechildren',
    labelKey: 'products:fields.includeChildren',
  },
  {
    key: 'categoryId',
    section: 'classification',
    moscow: 'must',
    requiredAtCreate: true,
    netSuiteField: 'class',
    labelKey: 'products:fields.category',
  },
  {
    key: 'secondaryCategoryIds',
    section: 'classification',
    moscow: 'could',
    requiredAtCreate: false,
    labelKey: 'products:fields.secondaryCategories',
  },

  // -- Descriptions 描述 (Tier-2 — ALL THREE gate 產品就緒) -----------------
  {
    key: 'salesDescription',
    section: 'descriptions',
    moscow: 'must',
    requiredAtCreate: false,
    requiredForReady: 'descriptions',
    netSuiteField: 'salesdescription',
    labelKey: 'products:fields.salesDescription',
    isFilled: (p) => filledString(p.salesDescription),
  },
  {
    key: 'purchaseDescription',
    section: 'descriptions',
    moscow: 'must',
    requiredAtCreate: false,
    requiredForReady: 'descriptions',
    netSuiteField: 'purchasedescription',
    labelKey: 'products:fields.purchaseDescription',
    isFilled: (p) => filledString(p.purchaseDescription),
  },
  {
    // PENDING-RULING(0708-J): NO xlsx row exists for a customs description
    // (searched all 272 data rows) — a house-invented third description. Tier
    // AND the descriptions readiness gate FROZEN pending Melissa's Q-M2
    // ruling (readiness output is snapshot-pinned); demotion = one-line edit.
    key: 'customsDescription',
    section: 'descriptions',
    moscow: 'must',
    requiredAtCreate: false,
    requiredForReady: 'descriptions',
    netSuiteField: 'custitem_customs_description',
    labelKey: 'products:fields.customsDescription',
    isFilled: (p) => filledString(p.customsDescription),
  },
  {
    // DD!46 採購單備註 (0708-J col-J SHOULD; col-L said Could — superseded).
    // Y-S3 yellow-sweep net-new custom field: buying note that flows onto the
    // vendor-PO doc endpoint like `purchaseDescription`, but should-tier —
    // NEVER gates create or 產品就緒.
    key: 'purchaseOrderNote',
    section: 'descriptions',
    moscow: 'should',
    requiredAtCreate: false,
    labelKey: 'products:fields.purchaseOrderNote',
    isFilled: (p) => filledString(p.purchaseOrderNote),
  },

  // -- Notes 備註 (0708-J alignment — Must, never gate 產品就緒) -------------
  {
    // US-183 商品備註 — schema+CRUD only; operation pop-up deferred (owner: Sherl)
    key: 'productNotes',
    section: 'descriptions',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.productNotes',
    isFilled: (p) => (p.productNotes?.length ?? 0) > 0,
  },
  {
    key: 'damageStockNotes',
    section: 'descriptions',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.damageStockNotes',
    isFilled: (p) => filledString(p.damageStockNotes),
  },
  {
    key: 'itemComment',
    section: 'descriptions',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.itemComment',
    isFilled: (p) => filledString(p.itemComment),
  },

  // -- Attributes 屬性 ------------------------------------------------------
  {
    key: 'oemNo',
    section: 'attributes',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'custitem_oem_no',
    labelKey: 'products:fields.oemNo',
  },
  {
    key: 'isActive',
    section: 'attributes',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'isinactive',
    labelKey: 'products:fields.isActive',
  },
  {
    // DD!108 Age Group 車齡組別 (col-J Must) — C108 inline 【MUST】: the user
    // enters vehicle start/end year and the SYSTEM computes the Age Group
    // (pure `computeAgeGroup`, S1.1). No longer directly enterable, so
    // "filled" = the driving year range is present, not the stored label.
    key: 'ageGroup',
    section: 'attributes',
    moscow: 'must',
    requiredAtCreate: false,
    systemAssigned: true,
    labelKey: 'products:fields.ageGroup',
    isFilled: (p) => typeof p.ageStartYear === 'number' && typeof p.ageEndYear === 'number',
  },

  // -- Fitment 車型適配 (cascade C3 — Make/Model both multi-select, R26) ----
  {
    key: 'fitment.makes',
    section: 'fitment',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'custitem_make',
    labelKey: 'products:fields.fitmentMakes',
    isFilled: (p) => (p.fitment?.makes.length ?? 0) > 0,
  },
  {
    key: 'fitment.models',
    section: 'fitment',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'custitem_model',
    labelKey: 'products:fields.fitmentModels',
    isFilled: (p) => (p.fitment?.models.length ?? 0) > 0,
  },
  {
    // 0708-J promotion should→must
    key: 'fitment.years',
    section: 'fitment',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'custitem_year',
    labelKey: 'products:fields.fitmentYears',
    isFilled: (p) => (p.fitment?.years.length ?? 0) > 0,
  },
  {
    // DD!31 Variant 版本/車型代別 (col-J Must) — product-level Car Details
    // field. Distinct from the per-year-range trim note
    // `ProductFitmentYearRange.variant` (Q-T3 may merge them); this row keeps
    // DD!31 visible to must-tracking either way.
    key: 'variant',
    section: 'fitment',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.variant',
  },
  {
    // DD!32 Extract Year 拆解年份 / 年份索引 (col-J Must) — year index in the
    // C108 age family (ageStartYear/ageEndYear → computed ageGroup).
    key: 'extractYear',
    section: 'fitment',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.extractYear',
  },

  // -- Manufacturing 製造 ---------------------------------------------------
  {
    // 0708-J new Must — 製造商, distinct from mpn/countryOfManufacture and vendor sourcing
    key: 'manufacturer',
    section: 'manufacturing',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'manufacturer',
    labelKey: 'products:fields.manufacturer',
  },
  {
    // 0708-J promotion should→must
    key: 'mpn',
    section: 'manufacturing',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'mpn',
    labelKey: 'products:fields.mpn',
  },
  {
    // 0708-J promotion should→must
    key: 'countryOfManufacture',
    section: 'manufacturing',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'countryofmanufacture',
    labelKey: 'products:fields.countryOfManufacture',
  },
  {
    // DD!69 FA Sub Category FA子分類 (col-J Must) — Purchasing/Manufacturing
    // sub-classification; absent from the codex-era set.
    key: 'faSubCategory',
    section: 'manufacturing',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.faSubCategory',
  },

  // -- Commercial & physical 商務與物理 (dims → M6 volumetric, D7) ----------
  {
    key: 'lengthMm',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'custitem_length_mm',
    labelKey: 'products:fields.lengthMm',
  },
  {
    key: 'widthMm',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'custitem_width_mm',
    labelKey: 'products:fields.widthMm',
  },
  {
    key: 'heightMm',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'custitem_height_mm',
    labelKey: 'products:fields.heightMm',
  },
  {
    key: 'weightKg',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'weight',
    labelKey: 'products:fields.weightKg',
  },
  {
    // 0708-J new Must — Item Weight Unit qualifier for weightKg (default kg)
    key: 'weightUnit',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'weightunit',
    labelKey: 'products:fields.weightUnit',
  },
  {
    // PENDING-RULING(0708-J): DD!107 col-J = 要允許不同區開同樣的欄位
    // (instruction, not Must — the same volumetric datum may render in >1
    // section, D107) and DD!109 Cubic Feet col-J = Should. Tier FROZEN as
    // must pending Melissa's Q-M2 ruling; demotion = one-line edit.
    key: 'cubicFeet',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    systemAssigned: true,
    netSuiteField: 'custitem_cubic_feet_calc',
    labelKey: 'products:fields.cubicFeet',
  },
  {
    // PENDING-RULING(0708-J): DD!40 Costing Method col-J = SHOULD (the must
    // came from superseded col-K residue). Tier AND the costingRef readiness
    // gate FROZEN pending Q-M2 — demotion changes computeProductReadiness.
    key: 'costingMethod',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    requiredForReady: 'costingRef',
    netSuiteField: 'costingmethod',
    labelKey: 'products:fields.costingMethod',
    isFilled: (p) => p.costingMethod != null,
  },
  {
    // PENDING-RULING(0708-J): DD!140 Base Price col-J = Should; DD!246 Item
    // Base Price col-J = CLARIFY; D47: List Price unnecessary once the
    // Pricing Engine is fully designed (rule with the Wave 3–5 plan). Tier
    // AND the pricingRef readiness gate FROZEN pending Q-M2.
    key: 'basePrice',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    requiredForReady: 'pricingRef',
    netSuiteField: 'baseprice',
    labelKey: 'products:fields.basePrice',
    isFilled: (p) => filledPositiveNumber(p.basePrice),
  },
  {
    // PENDING-RULING(0708-J): no col-J Must currency row exists — nearest are
    // DD!128–131 currency tabs (使用者選擇) and DD!256 (J = Won't). Tier
    // FROZEN as must pending Q-M2.
    key: 'currency',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'currency',
    labelKey: 'products:fields.currency',
  },
  {
    // 0708-J promotion should→must
    key: 'itemDefinedCost',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'cost',
    labelKey: 'products:fields.itemDefinedCost',
  },
  {
    // DD!98 Item Defined Cost Unit 品項自訂成本單位 (col-J Must) — unit
    // qualifier of `itemDefinedCost` (same pattern as weightUnit↔weightKg).
    key: 'itemDefinedCostUnit',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.itemDefinedCostUnit',
  },
  {
    // 0708-J promotion should→must
    key: 'minQty',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'minimumquantity',
    labelKey: 'products:fields.minQty',
  },
  {
    // DD!100 Minimum Quantity Unit 最小銷售數量單位 (col-J Must) — unit
    // qualifier of `minQty`.
    key: 'minQtyUnit',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.minQtyUnit',
  },
  {
    // 0708-J promotion could→must
    key: 'maxQty',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.maxQty',
  },
  {
    // DD!102 Maximum Quantity Unit 最大銷售數量單位 (col-J Must) — unit
    // qualifier of `maxQty`.
    key: 'maxQtyUnit',
    section: 'commercial',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.maxQtyUnit',
  },

  // -- Accounting 會計 (C7 — all 13 placeholders day one, R217) -------------
  {
    key: 'accounting.incomeAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'incomeaccount',
    labelKey: 'products:fields.incomeAccount',
  },
  {
    key: 'accounting.cogsAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'cogsaccount',
    labelKey: 'products:fields.cogsAccount',
  },
  {
    key: 'accounting.assetAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'assetaccount',
    labelKey: 'products:fields.assetAccount',
  },
  {
    key: 'accounting.gainLossAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'gainlossaccount',
    labelKey: 'products:fields.gainLossAccount',
  },
  {
    key: 'accounting.priceVarianceAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'pricevarianceacct',
    labelKey: 'products:fields.priceVarianceAccount',
  },
  {
    key: 'accounting.quantityVarianceAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'quantityvarianceacct',
    labelKey: 'products:fields.quantityVarianceAccount',
  },
  {
    key: 'accounting.exchangeRateVarianceAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'exchangeratevarianceacct',
    labelKey: 'products:fields.exchangeRateVarianceAccount',
  },
  {
    // 0708-J new Must (missing from the codex-era set) — R217 placeholder
    key: 'accounting.customerReturnVarianceAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'custreturnvarianceaccount',
    labelKey: 'products:fields.customerReturnVarianceAccount',
  },
  {
    // 0708-J new Must (missing from the codex-era set) — R217 placeholder
    key: 'accounting.vendorReturnVarianceAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'vendreturnvarianceaccount',
    labelKey: 'products:fields.vendorReturnVarianceAccount',
  },
  {
    // 0708-J new Must (missing from the codex-era set) — R217 placeholder
    key: 'accounting.dropshipExpenseAccountId',
    section: 'accounting',
    moscow: 'must',
    requiredAtCreate: false,
    netSuiteField: 'dropshipexpenseaccount',
    labelKey: 'products:fields.dropshipExpenseAccount',
  },
  {
    // @deprecated codex-era — in neither Must set (0708-J); kept, not deleted
    key: 'accounting.deferredRevenueAccountId',
    section: 'accounting',
    moscow: 'could',
    requiredAtCreate: false,
    netSuiteField: 'deferredrevenueaccount',
    labelKey: 'products:fields.deferredRevenueAccount',
  },
  {
    // DD!229 Intercompany Income Account, col-J = COULD，STERLING有用到，調貨用
    // (Sterling transfer use) — was CLARIFY under superseded col-K.
    // S8.1 (財務資料 confirm pass) lifted `blocked` TOGETHER with the
    // `accountingPlaceholdersSchema` + defaults + hydrate coverage, so the
    // S0.3 drift class (rendered field silently dropped on save) cannot recur.
    key: 'accounting.intercompanyIncomeAccountId',
    section: 'accounting',
    moscow: 'could',
    requiredAtCreate: false,
    netSuiteField: 'intercoincomeaccount',
    labelKey: 'products:fields.intercompanyIncomeAccount',
  },
  {
    // DD!228 Intercompany COGS Account, col-J = COULD，STERLING有用到，調貨用
    // — was CLARIFY under superseded col-K; `blocked` lifted in S8.1 with zod
    // schema coverage (see DD!229 note).
    key: 'accounting.intercompanyCogsAccountId',
    section: 'accounting',
    moscow: 'could',
    requiredAtCreate: false,
    netSuiteField: 'intercocogsaccount',
    labelKey: 'products:fields.intercompanyCogsAccount',
  },
  {
    // @deprecated codex-era — in neither Must set (0708-J); kept, not deleted
    key: 'accounting.vendorBillPriceVarianceAccountId',
    section: 'accounting',
    moscow: 'could',
    requiredAtCreate: false,
    netSuiteField: 'billpricevarianceacct',
    labelKey: 'products:fields.vendorBillPriceVarianceAccount',
  },
  {
    // @deprecated codex-era — in neither Must set (0708-J); kept, not deleted
    key: 'accounting.vendorBillQuantityVarianceAccountId',
    section: 'accounting',
    moscow: 'could',
    requiredAtCreate: false,
    netSuiteField: 'billqtyvarianceacct',
    labelKey: 'products:fields.vendorBillQuantityVarianceAccount',
  },
  {
    // @deprecated codex-era — in neither Must set (0708-J); kept, not deleted
    key: 'accounting.vendorBillExchangeRateVarianceAccountId',
    section: 'accounting',
    moscow: 'could',
    requiredAtCreate: false,
    netSuiteField: 'billexchratevarianceacct',
    labelKey: 'products:fields.vendorBillExchangeRateVarianceAccount',
  },
  {
    // DD!227 Tax Schedule, col-J = COULD，STERLING有用到，調貨用 — was CLARIFY
    // under superseded col-K; `blocked` lifted in S8.1 with zod schema
    // coverage (see DD!229 note).
    key: 'accounting.taxScheduleId',
    section: 'accounting',
    moscow: 'could',
    requiredAtCreate: false,
    netSuiteField: 'taxschedule',
    labelKey: 'products:fields.taxSchedule',
  },

  // -- Replenishment planning 補貨參數 (SR0.2 — DD!53–67, J=SHOULD) ---------
  // All SHOULD-tier: requiredAtCreate:false + no requiredForReady → this group
  // NEVER gates create OR readiness (Fixed decision 1). netSuiteField carries
  // the DD!53–67 dictionary row (NetSuite item field id); zh labels are the
  // lint-safe relabels (OQ-4). The dormant *Auto booleans + preferredStockLevel
  // days live on the type + handler passthrough only (rendered nowhere until
  // v2) and get no registry row.
  {
    key: 'planning.reorderPoint',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'reorderpoint',
    labelKey: 'products:fields.planning.reorderPoint',
  },
  {
    key: 'planning.preferredStockLevel',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'preferredstocklevel',
    labelKey: 'products:fields.planning.preferredStockLevel',
  },
  {
    key: 'planning.reorderMultiple',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'reordermultiple',
    labelKey: 'products:fields.planning.reorderMultiple',
  },
  {
    key: 'planning.purchaseLeadTimeDays',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'leadtime',
    labelKey: 'products:fields.planning.purchaseLeadTimeDays',
  },
  {
    key: 'planning.safetyStockDays',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'safetystocklevel',
    labelKey: 'products:fields.planning.safetyStockDays',
  },
  {
    key: 'planning.seasonalDemand',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'seasonaldemand',
    labelKey: 'products:fields.planning.seasonalDemand',
    isFilled: (p) => p.planning?.seasonalDemand === true,
  },
  {
    key: 'planning.expectedDemandChangePct',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'expecteddemandchange',
    labelKey: 'products:fields.planning.expectedDemandChangePct',
  },
  {
    key: 'planning.doNotOrder',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'donotreorder',
    labelKey: 'products:fields.planning.doNotOrder',
    isFilled: (p) => p.planning?.doNotOrder === true,
  },
  {
    key: 'planning.preOrder',
    section: 'planning',
    moscow: 'should',
    requiredAtCreate: false,
    netSuiteField: 'preorder',
    labelKey: 'products:fields.planning.preOrder',
    isFilled: (p) => p.planning?.preOrder === true,
  },

  // -- Relations 關聯側徑 (drawer — never overload the Create body) ---------
  {
    key: 'customerPartNumbers',
    section: 'relations',
    moscow: 'should',
    requiredAtCreate: false,
    labelKey: 'products:fields.customerPartNumbers',
    isFilled: (p) => (p.customerPartNumbers?.length ?? 0) > 0,
  },
  {
    // 備件料號 (2026-08-27 session §5) — party-less external identifier,
    // sibling of customerPartNumbers; never gates create or 產品就緒.
    key: 'sparePartNumbers',
    section: 'relations',
    moscow: 'should',
    requiredAtCreate: false,
    labelKey: 'products:fields.sparePartNumbers',
    isFilled: (p) => (p.sparePartNumbers?.length ?? 0) > 0,
  },
  {
    key: 'vendorLines',
    section: 'relations',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.vendorLines',
    isFilled: (p) => (p.vendorLines?.length ?? 0) > 0,
  },
  {
    // 0708-J promotion could→must
    key: 'images',
    section: 'relations',
    moscow: 'must',
    requiredAtCreate: false,
    labelKey: 'products:fields.images',
    isFilled: (p) => (p.images?.length ?? 0) > 0,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Default presence check for rules without a custom `isFilled`. */
export function isProductFieldFilled(product: Product, rule: ProductFieldRule): boolean {
  if (rule.isFilled) return rule.isFilled(product);
  const value = (product as unknown as Record<string, unknown>)[rule.key];
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * Two-tier readiness gate, computed on READ — never stored, never blocks save.
 * Product Ready 產品就緒 = create set + 3 descriptions + basePrice +
 * costingMethod; only Ready products enter SO/PO.
 */
export function computeProductReadiness(product: Product): ProductReadiness {
  const missing: ProductReadinessGap[] = [];
  for (const rule of PRODUCT_FIELD_REGISTRY) {
    if (!rule.requiredForReady) continue;
    if (!isProductFieldFilled(product, rule) && !missing.includes(rule.requiredForReady)) {
      missing.push(rule.requiredForReady);
    }
  }
  return { ready: missing.length === 0, missing };
}

export function getCreateRequiredRules(): ProductFieldRule[] {
  return PRODUCT_FIELD_REGISTRY.filter((rule) => rule.requiredAtCreate);
}

export function getRulesBySection(section: ProductFieldSection): ProductFieldRule[] {
  return PRODUCT_FIELD_REGISTRY.filter((rule) => rule.section === section);
}

export function getBlockedRules(): ProductFieldRule[] {
  return PRODUCT_FIELD_REGISTRY.filter((rule) => rule.blocked === true);
}

/**
 * required-iff-共用 attention rows (DD!19 看SCN狀況, FD6): the rows whose
 * `requiredIfShared` demand is unmet for this product — i.e. `isShared` is
 * true and the field is unfilled. An ATTENTION state for the owning section
 * (shared + unlinked ⇒ attention; shared + linked ⇒ none; unshared ⇒ none).
 * NEVER feeds `computeProductReadiness` — SCN never gates 產品就緒
 * (escalation = Q-O2).
 */
export function getAttentionRules(product: Product): ProductFieldRule[] {
  if (product.isShared !== true) return [];
  return PRODUCT_FIELD_REGISTRY.filter(
    (rule) => rule.requiredIfShared === true && !isProductFieldFilled(product, rule)
  );
}
